// @flow
import React from 'react';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { SecretaryForm as SecretaryFormView } from '@views';
import type { User, SecretariesState, BundledPermissions } from '@types';
import { router } from '@router';
import { clinicActions } from '@context/clinics';
import { fetchTeam, selectTeam, selectTeamMember } from '@context/team';
import { isStoreReview } from '../redux/modules/appVersion';
import { logout } from '@context/user';
import { mapBundledPermission, mapManagesClinic } from '@helpers/connect';


type Props = {
  user: User,
  id: number,
  secretaries: SecretariesState,
  dispatch: Function,
  permissions: BundledPermissions,
  isDeleting: boolean,
  isManager: boolean,
  // The secretary as Equipe knows them (null until the team loads)
  member: ?Object,
  teamLoaded: boolean,
  canInviteSecretary: boolean,
  storeReview: boolean,
};

class SecretaryForm extends React.Component<Props> {
  componentDidMount() {
    const { dispatch, isManager, teamLoaded, id } = this.props;
    if (isManager && !teamLoaded) dispatch(fetchTeam());
    // A member invited elsewhere is in Equipe before the clinic's data has them
    if (id !== 'nova' && isEmpty(this.formData())) dispatch(clinicActions.getClinics());
  }


  render() {
    const {
      user, secretaries, id, isDeleting, isManager, member, canInviteSecretary, storeReview,
    } = this.props;
    const isEdit = id !== 'nova';
    const isOwner = Boolean(member && member.owner);
    // Removing or demoting oneself here would leave the app on a clinic it can't use
    const isSelf = Boolean(member && member.userId === get(user, 'id'));
    // The owner is always a manager: locked until Equipe says who owns the clinic
    const managerLocked = isEdit && (!member || isOwner || isSelf);
    const formData = isEdit ? this.formData() : {};
    return (
      <SecretaryFormView
        key={isEmpty(formData) ? 'loading' : 'loaded'}
        isEdit={isEdit}
        user={user}
        formData={formData}
        isLoading={get(secretaries, 'isLoading', false) || get(secretaries, 'isSaving', false)}
        isDeleting={isDeleting}
        onCancel={this.handleCancel}
        onSave={this.handleSave}
        onLogout={this.handleLogout}
        permissions={this.props.permissions}
        isManager={isManager}
        isOwner={isOwner}
        isSelf={isSelf}
        managerLocked={managerLocked}
        inviteLocked={!isEdit && !canInviteSecretary}
        storeReview={storeReview}
        onDelete={isEdit && member && !isOwner && !isSelf ? this.handleDelete : null}
      />
    );
  }

  formData = () => get(this.props.secretaries, ['data', String(this.props.id)], {});

  handleLogout = () => this.props.dispatch(logout());

  handleCancel = () => {
    this.props.dispatch(router.pushPath('team'));
  };

  handleDelete = () => {
    const { dispatch, id } = this.props;
    dispatch(clinicActions.deleteSecretary(id, (ok) => {
      if (ok) dispatch(router.pushPath('team'));
    }));
  };

  handleSave = (data: Object) => {
    const { dispatch, id } = this.props;
    if (id !== 'nova') {
      dispatch(clinicActions.updateSecretary(id, data, (ok) => {
        if (ok) this.props.dispatch(router.pushPath('team'));
      }));
    } else {
      dispatch(clinicActions.createSecretary(data, (ok) => {
        if (ok) {
          this.props.dispatch(router.pushPath('team'));
        }
      }));
    }
  };
}

const mapStateToProps = (state, ownProps) => ({
  user: state.context.user,
  secretaries: get(state, ['context', 'secretaries'], {}),
  id: get(ownProps, ['match', 'params', 'id']),
  permissions: mapBundledPermission(state),
  isDeleting: get(state, ['context', 'secretaries', 'isDeleting'], false),
  isManager: mapManagesClinic(state),
  member: selectTeamMember(state, 'Secretary', get(ownProps, ['match', 'params', 'id'])),
  teamLoaded: Boolean(selectTeam(state)),
  canInviteSecretary: get(selectTeam(state), 'canInviteSecretary', true),
  storeReview: isStoreReview(state),
});

export default connect<any, any, any, any, any, any>(
  mapStateToProps,
)(SecretaryForm);
